---
title: "Recruiter tools: screening and your booking link"
description: "The AI screening assistant that joins your calls as a note-taker or copilot, calendar auto-join, your booking link, and the Calendar tab in Settings."
category: "marketplace"
keywords:
  - "AI interview notes"
  - "screening assistant"
  - "recruiter booking link"
  - "calendar auto-join"
  - "automatic meeting notes"
  - "connect Google Calendar"
schema: "HowTo"
nav:
  section: "guides"
  order: 47
  parent: "/guides/marketplace"
---

Two tools live under **Tools** in your sidebar: a screening assistant for your candidate calls and a personal booking link so candidates pick their own slot. They work together — booked calls can get the assistant automatically.

## The screening assistant

Create a screen from **Tools → Screening**: pick the candidate, paste the Google Meet link (or pick an upcoming booking), and choose a mode. There is no time to type. If the link is one of your bookings or a meeting on your connected calendar, the bot joins when the call starts; otherwise it joins right away and waits to be let in — so paste an ad-hoc link when you're about to start, not the night before. The dialog says which it found under the link field before you create anything.

- **Note-taker** joins the meeting, records, transcribes, and scores the conversation. You do the interview; it does the paperwork.
- **Copilot** does all of that plus a live dashboard during the call — suggested probes, coverage tracking, evidence capture while you talk.

Either way you get a scored scorecard afterward: rubric scores, quoted highlights with timestamps, and a replay link. It's attached to the candidate, not lost in a drive folder.

### Where screens pay off

Once a candidate is on a role, any scored screen for them is one click to attach from the packet page — right up until Chosen releases it. Companies review packets with a watchable screen faster, and the score feeds your recruiter stats. Your screening notes stay private unless you explicitly share them in the packet.

Screening runs on askspresso under the hood. If it's not enabled for your workspace yet, the page says so honestly instead of spinning.

## Your booking link

**Tools → Calendar** gives you one short link — `chosenhq.com/book/your-name` — that candidates use to grab time with you.

Setup, once:

1. **Connect Google Calendar.** Slots come from your real availability; nothing works without it. Connect from the Calendar tool, or from **Settings → Calendar**, which is also where you reconnect or disconnect later.
2. **Set your hours.** Weekly availability, meeting length, buffers, and how much notice you need. Length is whatever you type — any whole number from 5 to 240 minutes, not a pick-list. Slots line up on the half hour for 30/60/90-minute calls and on the quarter hour for everything else.
3. **Turn it on** and copy the link.

What the candidate sees: one page with the meeting details, a month calendar where only the days you actually have time on are clickable, and that day's slots beside it in their own timezone, on a 12- or 24-hour clock — their pick, not yours.

When a candidate books, the event lands on your Google Calendar with a Meet link, both of you get the invite, and the candidate gets a manage link to cancel or rebook without emailing you. Double-bookings are checked against your live calendar at booking time.

### Settings → Calendar

The Google Calendar connection lives in **Settings → Calendar**: connect, reconnect when Google drops the grant, disconnect. Scheduling, the booking link and the note-taker all read from that one connection, so if slots look wrong, check here first.

### The note-taker is on by default

Every call booked through your link gets the screening assistant in note-taker mode. The booking page discloses the recording to the candidate before they confirm, and new invitees who aren't in your workspace yet are added as candidates, so the notes have somewhere to live. The switch on the Calendar tool turns it off for future bookings.

A call that was booked without it — the switch was off, or the booking predates it — shows an **Add note-taker** button in the upcoming list on the Calendar tool and in a callout on **Tools → Screening**. One click, no link to paste, no time to enter: the bot takes the booked start.

## Calendar auto-join

Auto-join sends the note-taker to meetings already on your calendar. Nothing to create, no link to paste. Switch it on in **Settings → Calendar** and Chosen reads your connected Google Calendar and dispatches the note-taker to the meetings that match your rule.

### Two things gate it

First, consent. You tick a box saying you'll tell participants a note-taker is joining. The switch stays off until you do, and Chosen re-checks that consent on the server before it enables anything.

Second, a candidate. Chosen matches an outside guest to a candidate by email. A meeting it can't match is skipped, not recorded — the notes need somewhere to land.

### Which meetings it joins

Pick one rule:

- **External guests** — joins when someone outside your workspace is on the invite. The default, and usually the right one.
- **Only meetings I organize** — joins your own meetings, skips the ones you were invited to.
- **All meetings** — every meeting with a video link.

Every rule still needs a video link on the invite and a matched candidate. All-day blocks, declined invites, cancelled events and meetings with no link are skipped under all three.

### The next 24 hours

Under the settings is a preview of what's coming and what Chosen decided about each meeting: joining, skipped because everyone is internal, skipped because there's no video link, waiting on a candidate, or already covered by a teammate whose auto-join has the same call. Each row carries the reason, so "why didn't the note-taker join?" is answerable without asking anyone.

You can override any single meeting — skip one the rule would join, include one it wouldn't — and pick the candidate for the meetings that are waiting. **Tools → Screening** repeats the waiting ones in a callout so you don't have to go looking.

**Sync now** re-reads the calendar immediately and shows its progress while it works. When Google refuses, you get the actual error rather than a spinner that never stops.

### Where the notes end up

On the candidate, same as every other interview. Open a candidate and the **Interviews & notes** panel lists every call Chosen has recorded for them — scheduled interviews, Workbench take-homes, screens you created, meetings the auto-join picked up — with the summary, a searchable transcript, the questions that were actually asked with timestamps, and the recording. Notes usually land a few minutes after the call ends; the panel says "Processing" until they do.

Turning auto-join off cancels the note-takers queued for future meetings. Calls already recorded stay exactly where they are.
